import type { AuthResponse, AuthTokens, AuthUser, LoginPayload, RegisterPayload } from '../auth/types';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3393/api';

// --- Token accessors (wired by AuthContext at mount) ---

let getTokens: () => AuthTokens | null = () => null;
let onTokenRefresh: (tokens: AuthTokens) => void = () => {};
let onAuthFailure: () => void = () => {};

export function setTokenAccessors(
  getter: typeof getTokens,
  refreshHandler: typeof onTokenRefresh,
  failureHandler: typeof onAuthFailure,
) {
  getTokens = getter;
  onTokenRefresh = refreshHandler;
  onAuthFailure = failureHandler;
}

// --- Error class ---

export class ApiError extends Error {
  statusCode: number;

  constructor(statusCode: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
  }

  get isNetworkError() {
    return this.statusCode === 0;
  }

  get isUnauthorized() {
    return this.statusCode === 401;
  }
}

// --- Refresh logic (single in-flight + cooldown) ---

let refreshPromise: Promise<AuthTokens> | null = null;
let lastRefreshFailure = 0;
const REFRESH_COOLDOWN_MS = 5_000;

async function refreshTokens(): Promise<AuthTokens> {
  if (refreshPromise) return refreshPromise;

  // Prevent retry storms: if we failed recently, don't try again
  if (Date.now() - lastRefreshFailure < REFRESH_COOLDOWN_MS) {
    throw new ApiError(401, 'Session expired');
  }

  refreshPromise = (async () => {
    const tokens = getTokens();
    if (!tokens?.refreshToken) throw new ApiError(401, 'No refresh token');

    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: tokens.refreshToken }),
    });

    if (!res.ok) {
      lastRefreshFailure = Date.now();
      throw new ApiError(res.status, 'Token refresh failed');
    }

    const body = await res.json();
    const data: AuthResponse = body.data;
    const newTokens: AuthTokens = { accessToken: data.accessToken, refreshToken: data.refreshToken };
    onTokenRefresh(newTokens);
    return newTokens;
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

// --- Core fetch wrapper ---

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const tokens = getTokens();
  const headers = new Headers(options?.headers);
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (tokens?.accessToken) {
    headers.set('Authorization', `Bearer ${tokens.accessToken}`);
  }

  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, { ...options, headers });
  } catch (err) {
    throw new ApiError(0, err instanceof Error ? err.message : 'Network error');
  }

  // Auto-refresh on 401
  if (res.status === 401 && tokens?.refreshToken) {
    try {
      const newTokens = await refreshTokens();
      headers.set('Authorization', `Bearer ${newTokens.accessToken}`);
      res = await fetch(`${BASE_URL}${path}`, { ...options, headers });
    } catch {
      onAuthFailure();
      throw new ApiError(401, 'Session expired');
    }

    // If retried request also fails, throw but do NOT call onAuthFailure again
    if (!res.ok) {
      let message = res.statusText;
      try {
        const body = await res.json();
        message = body.message || message;
      } catch { /* use statusText */ }
      throw new ApiError(res.status, message);
    }
  } else if (!res.ok) {
    let message = res.statusText;
    try {
      const body = await res.json();
      message = body.message || message;
    } catch { /* use statusText */ }
    if (res.status === 401) onAuthFailure();
    throw new ApiError(res.status, message);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;

  const body = await res.json();
  return body.data;
}

// --- Typed API functions ---

export const authApi = {
  register: (payload: RegisterPayload) =>
    apiFetch<AuthResponse>('/auth/register', { method: 'POST', body: JSON.stringify(payload) }),

  login: (payload: LoginPayload) =>
    apiFetch<AuthResponse>('/auth/login', { method: 'POST', body: JSON.stringify(payload) }),

  google: (idToken: string) =>
    apiFetch<AuthResponse>('/auth/google', { method: 'POST', body: JSON.stringify({ idToken }) }),

  github: (code: string) =>
    apiFetch<AuthResponse>('/auth/github', { method: 'POST', body: JSON.stringify({ code }) }),

  me: () => apiFetch<AuthUser>('/auth/me'),

  logout: (refreshToken: string) =>
    apiFetch<void>('/auth/logout', { method: 'POST', body: JSON.stringify({ refreshToken }) }),
};

// --- Generation API ---

type GenerateBody = {
  prompt: string;
  workspaceId: string;
  mode?: 'mermaid' | 'excalidraw';
  imageBase64?: string;
  imageMediaType?: string;
};

type GenerateResult = {
  code?: string;
  elements?: unknown[];
  inputTokens: number;
  outputTokens: number;
};

type AutofixResult = {
  code: string;
};

export const generationApi = {
  generate: (diagramId: string, body: GenerateBody, signal?: AbortSignal) =>
    apiFetch<GenerateResult>(`/diagrams/${diagramId}/generate`, {
      method: 'POST',
      body: JSON.stringify(body),
      signal,
    }),

  autofix: (diagramId: string, body: { error: string; workspaceId: string }, signal?: AbortSignal) =>
    apiFetch<AutofixResult>(`/diagrams/${diagramId}/autofix`, {
      method: 'POST',
      body: JSON.stringify(body),
      signal,
    }),
};

// --- Workspace API ---

export const workspaceApi = {
  list: () => apiFetch<any[]>('/workspaces'),
  get: (id: string) => apiFetch<any>(`/workspaces/${id}`),
  create: (body: { name: string; slug?: string }) =>
    apiFetch<any>('/workspaces', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: { name?: string }) =>
    apiFetch<any>(`/workspaces/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  invite: (id: string, body: { email: string; role?: string }) =>
    apiFetch<any>(`/workspaces/${id}/invite`, { method: 'POST', body: JSON.stringify(body) }),
};

// --- Project API (nested under workspaces) ---

export const projectApi = {
  list: (workspaceId: string) => apiFetch<any[]>(`/workspaces/${workspaceId}/projects`),
  create: (workspaceId: string, body: { title: string }) =>
    apiFetch<any>(`/workspaces/${workspaceId}/projects`, { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: { title?: string }) =>
    apiFetch<any>(`/projects/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (id: string) =>
    apiFetch<void>(`/projects/${id}`, { method: 'DELETE' }),
};

// --- Diagram API (nested under projects) ---

export const diagramApi = {
  list: (projectId: string) => apiFetch<any[]>(`/projects/${projectId}/diagrams`),
  get: (id: string) => apiFetch<any>(`/diagrams/${id}`),
  create: (projectId: string, body: { title: string; type?: string; mermaidCode?: string; excalidrawData?: any; theme?: string; tags?: string[] }) =>
    apiFetch<any>(`/projects/${projectId}/diagrams`, { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: Record<string, any>) =>
    apiFetch<any>(`/diagrams/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (id: string) =>
    apiFetch<void>(`/diagrams/${id}`, { method: 'DELETE' }),
  sync: (projectId: string, body: { diagrams: any[] }) =>
    apiFetch<any>(`/projects/${projectId}/diagrams/sync`, { method: 'POST', body: JSON.stringify(body) }),
  share: (id: string) =>
    apiFetch<{ shareToken: string; isPublic: boolean }>(`/diagrams/${id}/share`, { method: 'POST' }),
  unshare: (id: string) =>
    apiFetch<{ isPublic: boolean }>(`/diagrams/${id}/share`, { method: 'DELETE' }),
};

// --- Plans API ---

export const plansApi = {
  list: () => apiFetch<any[]>('/plans'),
};

// --- Billing API ---

export const billingApi = {
  subscribe: (body: { workspaceId: string; planId: string }) =>
    apiFetch<{ url: string; subscriptionId: string }>('/billing/subscribe', { method: 'POST', body: JSON.stringify(body) }),
  cancel: (body: { workspaceId: string }) =>
    apiFetch<void>('/billing/cancel', { method: 'POST', body: JSON.stringify(body) }),
  subscription: (workspaceId: string) =>
    apiFetch<any>(`/billing/subscription?workspaceId=${workspaceId}`),
  usage: (workspaceId: string) =>
    apiFetch<any>(`/billing/usage?workspaceId=${workspaceId}`),
};
